'use client';
import { useState } from 'react';

const STEPS = [
    { icon: 'pi pi-check-circle', label: 'Ticket Raised',     color: '#00d2be' },
    { icon: 'pi pi-cog',          label: 'Under Review',      color: '#f59e0b' },
    { icon: 'pi pi-wrench',       label: 'Repair In Progress', color: '#6366f1' },
    { icon: 'pi pi-box',          label: 'Ready / Shipped',   color: '#10b981' },
];

export const TrackStatusCard = () => {
    const [ticketId, setTicketId] = useState('');
    const [focused,  setFocused]  = useState(false);

    const handleTrack = () => {
        if (ticketId.trim()) window.location.href = `/support/track/${ticketId}`;
    };

    return (
        <section style={{ background: 'var(--primary-color)', padding: '4rem 1.5rem', position: 'relative', overflow: 'hidden' }}>
            <style>{`
                @keyframes ts-float {
                    0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.06; }
                    50%      { transform: translateY(-16px) rotate(6deg); opacity: 0.12; }
                }
                @keyframes ts-pulse {
                    0%   { transform: scale(1);   opacity: 0.4; }
                    100% { transform: scale(2.2); opacity: 0; }
                }
                .ts-float-icon { position: absolute; pointer-events: none; animation: ts-float ease-in-out infinite; }
                .ts-pulse-ring {
                    position: absolute; inset: -10px; border-radius: 50%;
                    border: 1.5px solid rgba(0,210,190,0.3);
                    animation: ts-pulse 2.6s ease-out infinite;
                    pointer-events: none;
                }
                .ts-pulse-ring-2 {
                    position: absolute; inset: -22px; border-radius: 50%;
                    border: 1px solid rgba(0,210,190,0.15);
                    animation: ts-pulse 2.6s ease-out 0.9s infinite;
                    pointer-events: none;
                }
                .ts-input {
                    flex: 1;
                    min-width: 0;
                    padding: 0.85rem 1rem 0.85rem 2.75rem;
                    border-radius: 0.75rem;
                    font-size: 0.92rem;
                    outline: none;
                    background: rgba(255,255,255,0.07);
                    color: #fff;
                    transition: all 0.2s;
                    font-family: inherit;
                }
                .ts-input::placeholder { color: rgba(255,255,255,0.35); }
                .ts-input:focus { background: rgba(255,255,255,0.12); }
                .ts-btn {
                    display: inline-flex; align-items: center; gap: 0.5rem;
                    background: #fff; color: var(--primary-color);
                    border: none; border-radius: 0.75rem;
                    padding: 0.85rem 1.75rem;
                    font-size: 0.92rem; font-weight: 800;
                    cursor: pointer; white-space: nowrap;
                    transition: all 0.2s;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
                }
                .ts-btn:hover { background: #f0f5ff; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.25); }
                .ts-step-row {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 0.75rem;
                    max-width: 680px;
                    width: 100%;
                    margin: 0 auto;
                }
                .ts-input-row {
                    display: flex; gap: 0.75rem; width: 100%; max-width: 560px;
                }
                @media (max-width: 640px) {
                    .ts-input-row { flex-direction: column; }
                    .ts-btn { width: 100%; justify-content: center; }
                    .ts-step-row { grid-template-columns: repeat(2, 1fr); }
                }
            `}</style>

            {/* Floating icons */}
            {[
                { icon: 'pi pi-wrench',       top: '10%', left: '4%',   size: '1.5rem', dur: '8s',  delay: '0s'   },
                { icon: 'pi pi-ticket',       top: '65%', left: '3%',   size: '1.1rem', dur: '10s', delay: '1.5s' },
                { icon: 'pi pi-search',       top: '20%', right: '5%',  size: '1.2rem', dur: '9s',  delay: '0.8s' },
                { icon: 'pi pi-check-circle', top: '70%', right: '4%',  size: '1rem',   dur: '7s',  delay: '2s'   },
                { icon: 'pi pi-box',          top: '40%', left: '2%',   size: '0.9rem', dur: '11s', delay: '3s'   },
                { icon: 'pi pi-envelope',     top: '80%', right: '12%', size: '1rem',   dur: '9.5s',delay: '1.2s' },
            ].map(({ icon, top, left, right, size, dur, delay }) => (
                <div key={icon + top} className="ts-float-icon" style={{ top, left, right, animationDuration: dur, animationDelay: delay }}>
                    <i className={icon} style={{ fontSize: size, color: 'rgba(255,255,255,0.18)' }} />
                </div>
            ))}

            {/* Dot grid */}
            <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
            }} />

            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2.5rem' }}>

                {/* ── Header ── */}
                <div style={{ textAlign: 'center' }}>
                    <div style={{ position: 'relative', width: '4.5rem', height: '4.5rem', margin: '0 auto 1.25rem' }}>
                        <div className="ts-pulse-ring" />
                        <div className="ts-pulse-ring-2" />
                        <div style={{
                            width: '4.5rem', height: '4.5rem', borderRadius: '50%',
                            background: 'rgba(0,210,190,0.15)',
                            border: '1.5px solid rgba(0,210,190,0.35)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            position: 'relative', zIndex: 1,
                        }}>
                            <i className="pi pi-wrench" style={{ fontSize: '1.75rem', color: '#00d2be' }} />
                        </div>
                    </div>

                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        background: 'rgba(0,210,190,0.1)', border: '1px solid rgba(0,210,190,0.3)',
                        color: '#00d2be', borderRadius: 999, padding: '0.25rem 0.85rem',
                        fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                        marginBottom: '0.75rem', display: 'inline-flex',
                    }}>
                        <i className="pi pi-bolt" style={{ fontSize: '0.55rem' }} /> Live Tracking
                    </span>

                    <h2 style={{ margin: '0.5rem 0 0.5rem', fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                        Track Repair Status
                    </h2>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.55)', fontSize: '0.92rem' }}>
                        Check the status of your repair or support ticket instantly
                    </p>
                </div>

                {/* ── Input row ── */}
                <div className="ts-input-row">
                    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                        <i className="pi pi-search" style={{
                            position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)',
                            fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', pointerEvents: 'none',
                        }} />
                        <input
                            className="ts-input"
                            type="text"
                            placeholder="Enter Ticket ID or Order ID"
                            value={ticketId}
                            onChange={e => setTicketId(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleTrack()}
                            onFocus={() => setFocused(true)}
                            onBlur={() => setFocused(false)}
                            style={{
                                border: `1.5px solid ${focused ? 'rgba(0,210,190,0.55)' : 'rgba(255,255,255,0.12)'}`,
                                width: '100%', boxSizing: 'border-box',
                            }}
                        />
                    </div>
                    <button className="ts-btn" onClick={handleTrack}>
                        <i className="pi pi-search" style={{ fontSize: '0.85rem' }} />
                        Track Now
                    </button>
                </div>

                {/* ── Status steps ── */}
                <div className="ts-step-row">
                    {STEPS.map(({ icon, label, color }, i) => (
                        <div key={label} style={{
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '0.875rem',
                            padding: '0.9rem 0.75rem',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem',
                            textAlign: 'center',
                        }}>
                            <div style={{
                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                background: `${color}18`,
                                border: `1px solid ${color}40`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <i className={icon} style={{ fontSize: '0.85rem', color }} />
                            </div>
                            <div style={{ fontSize: '0.68rem', fontWeight: 600, color: 'rgba(255,255,255,0.6)', lineHeight: 1.3 }}>
                                {label}
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Info line ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="pi pi-envelope" style={{ fontSize: '0.75rem', color: 'rgba(0,210,190,0.6)' }} />
                    <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>
                        You'll receive email updates on your repair status anytime.
                    </span>
                </div>
            </div>
        </section>
    );
};
